import {useEffect, useCallback} from 'react';
import {fabric} from 'fabric';

// eslint-disable-next-line no-underscore-dangle
const isImageObject = (obj) => obj && (obj.type === 'uYOImage' || obj._element);

export const useShadowImage = (canvas) => {
  const createShadowImage = useCallback(
    (target) => {
      if (!isImageObject(target)) return;

      // eslint-disable-next-line no-underscore-dangle
      const image = new fabric.Image(target._originalElement);
      image.set({
        ...target,
        isShadow: true,
        originalObjectId: target.id,
        opacity: 0.4,
        selectable: false,
        evented: false,
        clipPath: null,
        excludeFromExport: true,
      });

      const existingShadows = canvas
        .getObjects()
        .filter((obj) => obj.isShadow && obj.originalObjectId === target.id);
      existingShadows.forEach((shadow) => canvas.remove(shadow));

      canvas.add(image);
      canvas.renderAll();
    },
    [canvas]
  );

  const updateShadowPosition = useCallback(
    (target) => {
      if (!isImageObject(target)) return;

      const shadow = canvas
        .getObjects()
        .find((obj) => obj.isShadow && obj.originalObjectId === target.id);
      if (shadow) {
        shadow.set({
          left: target.left,
          top: target.top,
          scaleX: target.scaleX,
          scaleY: target.scaleY,
          angle: target.angle,
          width: target.width,
          height: target.height,
        });
        canvas.renderAll();
      }
    },
    [canvas]
  );

  const removeShadow = useCallback(
    (target) => {
      if (!isImageObject(target)) return;

      const shadows = canvas.getObjects().filter((obj) => obj.isShadow);
      if (shadows.length) {
        shadows.forEach((shadow) => canvas.remove(shadow));
        canvas.renderAll();
      }
    },
    [canvas]
  );

  useEffect(() => {
    if (!canvas) return;

    const handleSelectionCreated = (e) => {
      const target = e.selected?.[0] || canvas.getActiveObject();
      if (isImageObject(target)) {
        createShadowImage(target);
      }
    };

    const handleSelectionUpdated = (e) => {
      if (e.deselected?.length) {
        e.deselected.forEach(removeShadow);
      }
      const target = e.selected?.[0] || canvas.getActiveObject();
      if (isImageObject(target)) {
        createShadowImage(target);
      }
    };

    const handleSelectionCleared = (e) => {
      if (e.deselected?.length) {
        e.deselected.forEach(removeShadow);
      }
    };

    const handleObjectUpdate = ({target}) => {
      if (isImageObject(target)) {
        updateShadowPosition(target);
      }
    };

    const handleObjectModified = () => {
      const target = canvas.getActiveObject();
      if (isImageObject(target)) {
        updateShadowPosition(target);
      }
    };

    const handleObjectRemoved = (e) => {
      if (isImageObject(e.target)) {
        removeShadow(e.target);
      }
    };

    const events = {
      'selection:created': handleSelectionCreated,
      'selection:updated': handleSelectionUpdated,
      'selection:cleared': handleSelectionCleared,
      'object:removed': handleObjectRemoved,
      'object:moving': handleObjectUpdate,
      'object:scaling': handleObjectUpdate,
      'object:modified': handleObjectModified,
    };

    canvas.on(events);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off(events);
    };
  }, [canvas, createShadowImage, updateShadowPosition, removeShadow]);

  useEffect(
    () => () => {
      if (canvas) {
        const existingShadows = canvas.getObjects().filter((obj) => obj.isShadow);
        existingShadows.forEach((shadow) => canvas.remove(shadow));
        canvas.requestRenderAll();
      }
    },
    [canvas]
  );
};
